import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Magic-link consumer route. Extracts `token` from the URL, POSTs to
 * /api/auth/magic-link/verify, then hands the session off to the shared
 * AuthProvider before redirecting to /dashboard (or the payload's
 * `redirect_to`). Deliberately dumb — visually a spinner + status card so
 * broken tokens don't dump users on a blank screen.
 */
export default function AuthMagic() {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false); // guard against React 18 StrictMode double-invoke

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("Missing sign-in token. Request a new link from the sign-in screen.");
      return;
    }

    const API = process.env.REACT_APP_BACKEND_URL;
    axios
      .post(
        `${API}/api/auth/magic-link/verify`,
        { token },
        { withCredentials: true }
      )
      .then(async (r) => {
        setStatus("success");
        setMessage(
          r.data?.is_new_user
            ? "Welcome to MileageIQ. Setting up your garage…"
            : "Signed in. Redirecting…"
        );
        // Refresh the AuthProvider so ProtectedRoute sees the new user
        try {
          if (typeof checkAuth === "function") await checkAuth();
        } catch {
          /* AuthProvider will retry on next mount */
        }
        const next = r.data?.redirect_to || "/dashboard";
        setTimeout(() => navigate(next, { replace: true }), 600);
      })
      .catch((e) => {
        const detail =
          e?.response?.data?.detail ||
          "Couldn't sign you in with that link. Please request a new one.";
        setStatus("error");
        setMessage(typeof detail === "string" ? detail : "Sign-in failed.");
        toast.error(typeof detail === "string" ? detail : "Sign-in failed.");
      });
  }, [location.search, navigate, checkAuth]);

  return (
    <div
      data-theme="dark"
      className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)] flex items-center justify-center px-6"
      data-testid="auth-magic-page"
    >
      <div
        className="w-full max-w-[360px] bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-6 text-center"
        style={{ borderRadius: 4 }}
        data-testid={`auth-magic-${status}`}
      >
        {status === "verifying" && (
          <>
            <Loader2 className="w-6 h-6 mx-auto mb-3 text-[#E82127] animate-spin" strokeWidth={1.75} />
            <div className="label-tiny mb-1">Signing in</div>
            <p className="text-sm text-[var(--text-secondary)]">
              Verifying your magic link…
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-6 h-6 mx-auto mb-3 text-[#34D17A]" strokeWidth={1.75} />
            <div className="label-tiny mb-1">You&apos;re in</div>
            <p className="text-sm text-[var(--text-secondary)]">{message}</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-6 h-6 mx-auto mb-3 text-[#E82127]" strokeWidth={1.75} />
            <div className="label-tiny mb-1">Sign-in failed</div>
            <p className="text-sm text-[var(--text-secondary)] mb-4">{message}</p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="text-xs text-[var(--text-primary)] underline underline-offset-2 tap"
              data-testid="auth-magic-back-btn"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
